<template>
  <v-row>
    <v-dialog v-model="showDialog" scrollable max-width="70%" style="z-index: 1019;">
      <v-skeleton-loader v-if="loading" type="article, actions"></v-skeleton-loader>
      <v-card v-if="!loading">
        <v-card-title style="padding: 10px 0 0 10px">
          <v-col cols="12">
            <v-row justify="start" align="center">
              <span style="font-size: 20px;">
                {{ $t('wkt.admin.workplace.edit-holidays-window.title') }}
              </span>
            </v-row>
          </v-col>
        </v-card-title>
        <v-card-text style="padding-bottom: 0;">
          <v-container fluid @wheel="onWheel" style="padding-bottom: 60px;">
            <span style="color: black; font-size: 17px">
              {{ getWorkplaceNameById(workplaceId) }}
            </span>
            <div>
              <v-data-table
              :headers="headers"
              :loading="tableLoading"
              :items="data"
              @click:row="editItem"
              :loading-text="$t('wkt.admin.common.table.loading')"
              :no-data-text="$t('wkt.admin.common.table.no_data')"
              :no-results-text="$t('wkt.admin.common.table.no_results')"
              dense 
              fixed-header
              height="calc(100vh - 560px)"
              disable-pagination
              hide-default-footer
              class='elevation-0'
            >
                <template v-slot:[`item.date`]="{ item }">
                  {{ item.date.day }}/{{ item.date.month }}/{{ item.date.year }}
                </template>
                <template v-slot:[`item.dayType`]="{ item }">
                  {{ descriptionTypeHoliday(item.dayType) }}
                </template>
                <template v-slot:[`item.checkinAllowed`]="{ item }">
                  {{ item.checkinAllowed ? $t('wkt.admin.common.yes') : $t('wkt.admin.common.no') }}
                </template>
              </v-data-table>
            </div>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-tooltip v-if="!loading" left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                @click="addItem()"
                v-bind="attrs"
                v-on="on"
                elevation="2"
                fab
                color="#185EA9"
                style="position: absolute; bottom: 60px; right: 16px;"
              >
                <v-icon class="relative" color="#FFFFFF">add</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("wkt.admin.holidays.calendar.edit-holiday-window.new_record_tooltip") }}</span>
          </v-tooltip>
        </v-card-actions>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeAllDialogs" style="font-size: 0.9em; font-weight: 400">
            {{ $t("wkt.admin.common.dialog-buttons.close").toUpperCase() }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <HolidaysDialog ref='dlgHolidays' @update-data="handleUpdateData" />
  </v-row>
</template>

<script>
import BaseModule from '../../common/BaseModule.js';
import { Constants } from '../../common/Constants.js';
import api from '../../../api/worktime';
import HolidaysDialog from './HolidaysDialog.vue';
import gwt from '../../../api/gwt.js';

export default {
  name: 'HolidaysTableDialog',
  components: {
    HolidaysDialog
  },
  mixins: [BaseModule],
  data() {
    return {
      showDialog: false,
      loading: false,
      workplaceId: null,
      tableLoading: false,
      data: [],
      headers: [
        { text: this.$t('wkt.admin.workplace.edit-holidays-window.table.headers.date'), value: 'date', sortable: false },
        { text: this.$t('wkt.admin.workplace.edit-holidays-window.table.headers.type'), value: 'dayType', width: '25%', sortable: false },
        { text: this.$t('wkt.admin.workplace.edit-holidays-window.table.headers.description'), value: 'description', width: '70%', sortable: false },
        { text: this.$t('wkt.admin.workplace.edit-holidays-window.table.headers.checkin_allowed'), value: 'checkinAllowed', sortable: false }
      ]
    };
  },
  methods: {
    onWheel(event) {
      event.stopPropagation();
    },
    open(action, payload) {
      this.workplaceId = payload;
      this.showDialog = true;
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }

      this.getWorkplaceHolidays();
    },

    getWorkplaceHolidays() {
      this.loading = true;
      api.getWorkplaceHolidays(this.workplaceId, res => {
        this.loading = false;
        this.data = res.sort((holiday1, holiday2) => {
          const dateHoliday1 = new Date(holiday1.date.year, holiday1.date.month - 1, holiday1.date.day);
          const dateHoliday2 = new Date(holiday2.date.year, holiday2.date.month - 1, holiday2.date.day);
          return dateHoliday2 - dateHoliday1; // Orden descendente (fechas más recientes primero)
        });
      },
      error => {
        this.loading = false;
        this.$emit('show-alert', { text: error, color: 'error', timeout: 3000 });
      });
    },
    descriptionTypeHoliday(type) {
      switch (type) {
        case Constants.REST_DAY:
          return this.$t('wkt.admin.elements.workday-element.holiday-type-day.rest');
        case Constants.NATIONAL_HOLIDAY:
          return this.$t('wkt.admin.elements.workday-element.holiday-type-day.national');
        case Constants.LOCAL_HOLIDAY:
          return this.$t('wkt.admin.elements.workday-element.holiday-type-day.local');
        case Constants.COMPANY_HOLIDAY:
          return this.$t('wkt.admin.elements.workday-element.holiday-type-day.company');
        default:
          return '';
      }
    },
    addItem() {
      this.editItem(null);
    },
    editItem(item) {
      const companyId = gwt.getSessionInformation().company.id;
      this.$refs.dlgHolidays.open(this.workplaceId, companyId, item);
    },
    handleUpdateData() {
      this.tableLoading = true;

      api.getWorkplaceHolidays(this.workplaceId, res => {
        this.tableLoading = false;
        this.data = res.sort((holiday1, holiday2) => {
          const dateHoliday1 = new Date(holiday1.date.year, holiday1.date.month - 1, holiday1.date.day);
          const dateHoliday2 = new Date(holiday2.date.year, holiday2.date.month - 1, holiday2.date.day);
          return dateHoliday2 - dateHoliday1; // Orden descendente (fechas más recientes primero)
        });
      },
      error => {
        this.loading = false;
        this.$emit('show-alert', { text: error, color: 'error', timeout: 3000 });
      });
    },
    closeAllDialogs() {
      this.showDialog = false;
      this.$refs.dlgHolidays.cancelEdit();
    }
  },
}
</script>